'use client';

import { motion } from 'framer-motion';

interface ExperienceOrientationScreenProps {
  title?: string | null;
  body?: string | null;
  ctaLabel?: string | null;
  eyebrow?: string | null;
  disabled?: boolean;
  onContinue: () => void;
}

const stagger = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: 'easeOut' as const },
  }),
};

export function ExperienceOrientationScreen({
  title,
  body,
  ctaLabel,
  eyebrow = 'Up next',
  disabled = false,
  onContinue,
}: ExperienceOrientationScreenProps) {
  let idx = 0;
  return (
    <div className="flex flex-col items-center gap-8 px-6 py-12 text-center">
      <div className="flex w-full max-w-md flex-col items-center gap-4">
        {eyebrow ? (
          <motion.span
            custom={idx++}
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="font-body text-[12px] font-medium uppercase tracking-[0.18em] text-accent-dark"
          >
            {eyebrow}
          </motion.span>
        ) : null}
        {title ? (
          <motion.h1
            custom={idx++}
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="font-heading text-[32px] font-normal leading-[1.15] tracking-[-0.005em] text-text-primary"
          >
            {title}
          </motion.h1>
        ) : null}
        {body ? (
          <motion.p
            custom={idx++}
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="font-body text-[16px] leading-[1.6] text-text-secondary"
          >
            {body}
          </motion.p>
        ) : null}
      </div>
      <motion.button
        type="button"
        custom={idx++}
        initial="hidden"
        animate="visible"
        variants={stagger}
        whileTap={disabled ? undefined : { scale: 0.97 }}
        onClick={onContinue}
        disabled={disabled}
        className="inline-flex items-center justify-center rounded-[12px] bg-accent-dark px-9 py-3.5 font-body text-[17px] font-semibold tracking-[0.01em] text-[var(--cream)] cursor-pointer transition-shadow duration-200 hover:shadow-[0_4px_14px_rgba(36,114,114,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {ctaLabel || 'Continue'}
      </motion.button>
    </div>
  );
}
